import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Settings2 } from "lucide-react";
import type { AppSettings, AuditLogListResponse } from "../../shared/types";
import { api, jsonBody } from "../lib/api";
import { date } from "../lib/format";
import { PageHeader } from "../components/app-shell";
import { Button, Empty, ErrorState, Field, Loading, Toggle, useToast } from "../components/ui";
import { useSession } from "../context/session";

export function SettingsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useSession();
  const query = useQuery({ queryKey: ["settings"], queryFn: () => api<AppSettings>("/api/settings") });
  const [auditPage, setAuditPage] = useState(1);
  const auditQuery = useQuery({ queryKey: ["audit", auditPage], queryFn: () => api<AuditLogListResponse>(`/api/audit?page=${auditPage}&pageSize=10`) });
  const [form, setForm] = useState<AppSettings | null>(null);
  const [emailDomains, setEmailDomains] = useState("");
  useEffect(() => {
    if (!query.data) return;
    // Server data is the reset point for this deliberately local, editable draft.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForm(query.data);
    setEmailDomains(query.data.allowedEmailDomains.join("\n"));
  }, [query.data]);
  const mutation = useMutation({
    mutationFn: (settings: Partial<AppSettings>) => api<AppSettings>("/api/settings", { method: "PATCH", ...jsonBody(settings) }),
    async onSuccess(settings) {
      setForm(settings);
      toast.push("系统设置已保存");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["settings"] }),
        queryClient.invalidateQueries({ queryKey: ["public-config"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
    onError: toast.fromError,
  });

  const save = () => {
    if (!form) return;
    const next = {
      ...form,
      allowedEmailDomains: [...new Set(emailDomains.split(/[\s,]+/).map((value) => value.trim().toLowerCase()).filter(Boolean))],
    };
    if (user?.role === "super_admin") {
      mutation.mutate(next);
      return;
    }
    const { apiDailyLimit: _apiDailyLimit, ...adminEditable } = next;
    mutation.mutate(adminEditable);
  };
  return (
    <div className="page settings-page">
      <PageHeader eyebrow="SYSTEM · 设置" title="运行规则" body="开放范围、审核姿态和生命周期数值都从这里进入同一份服务端策略。" actions={<Button variant="primary" onClick={save} disabled={!form || mutation.isPending}><Save size={15} />保存设置</Button>} />
      {query.isLoading ? <Loading label="读取设置" /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : form ? (
        <div className="settings-layout">
          <div className="settings-form">
            <SettingsSection index="01" eyebrow="ACCESS" title="开放与维护">
              <SettingRow title="开放注册" body="关闭时，未登录访客只看到维护页；管理员登录入口仍然可用。">
                <Toggle checked={form.registrationOpen} onChange={(registrationOpen) => setForm({ ...form, registrationOpen })} label={form.registrationOpen ? "已开放" : "已关闭"} />
              </SettingRow>
              <SettingRow title="维护模式" body="用于紧急维护。已登录管理员仍可进入控制台处理问题。">
                <Toggle checked={form.maintenanceMode} onChange={(maintenanceMode) => setForm({ ...form, maintenanceMode })} label={form.maintenanceMode ? "维护中" : "正常"} />
              </SettingRow>
              <Field label="允许注册的邮箱域名 · ONE PER LINE" hint="已预置 Gmail、QQ、163、126、Outlook、iCloud 等主要长期邮箱；系统临时邮箱域名即使误填也会被拒绝。">
                <textarea className="input textarea" value={emailDomains} onChange={(event) => setEmailDomains(event.target.value)} placeholder={"example.com\n*.company.example"} />
              </Field>
            </SettingsSection>
            <SettingsSection index="02" eyebrow="REVIEW" title="审核姿态">
              <div className="posture-grid">
                {([
                  ["allowlist", "白名单直通", "命中白名单直接生效，其余进入待审。"],
                  ["all", "全部先审", "所有普通用户链接都需要人工决定。"],
                  ["off", "关闭审核", "链接创建后立即生效，仅保留域名拦截规则。"],
                ] as const).map(([value, title, body]) => (
                  <button key={value} type="button" className={form.reviewPosture === value ? "posture-card posture-card--selected" : "posture-card"} onClick={() => setForm({ ...form, reviewPosture: value })}><span>{form.reviewPosture === value ? "●" : "○"}</span><strong>{title}</strong><p>{body}</p></button>
                ))}
              </div>
            </SettingsSection>
            <SettingsSection index="03" eyebrow="LINK POLICY" title="后缀与有效期">
              <div className="settings-number-grid">
                <NumberField label="用户默认有效期" suffix="天" value={form.defaultUserTtlDays} min={1} max={3650} onChange={(defaultUserTtlDays) => setForm({ ...form, defaultUserTtlDays })} />
                <NumberField label="宽限期" suffix="天" value={form.gracePeriodDays} min={0} max={365} onChange={(gracePeriodDays) => setForm({ ...form, gracePeriodDays })} />
                <NumberField label="释放冷却" suffix="天" value={form.releasedCooldownDays} min={0} max={365} onChange={(releasedCooldownDays) => setForm({ ...form, releasedCooldownDays })} />
                <NumberField label="用户后缀长度" suffix="位" value={form.userSlugLength} min={4} max={16} onChange={(userSlugLength) => setForm({ ...form, userSlugLength })} />
                <NumberField label="VIP 指定短号长度" suffix="位" value={form.vipSlugLength} min={4} max={16} onChange={(vipSlugLength) => setForm({ ...form, vipSlugLength })} />
                <NumberField label="管理员数字号池" suffix="号" value={form.adminPoolEnd} min={9} max={9999} onChange={(adminPoolEnd) => setForm({ ...form, adminPoolEnd })} />
                <NumberField label="批量上限" suffix="条" value={form.maxBatchSize} min={1} max={200} onChange={(maxBatchSize) => setForm({ ...form, maxBatchSize })} />
              </div>
              <SettingRow title="允许用户自定义后缀" body={`开启后仍要求至少 ${form.userSlugLength} 位；管理员极短号池永远不会向普通用户开放。`}>
                <Toggle checked={form.allowUserCustomSlug} onChange={(allowUserCustomSlug) => setForm({ ...form, allowUserCustomSlug })} label={form.allowUserCustomSlug ? "允许" : "随机分配"} />
              </SettingRow>
            </SettingsSection>
            <SettingsSection index="04" eyebrow="API QUOTA" title="接口调用额度">
              <SettingRow title="API 每日限额" body="按 API 令牌、按 UTC 自然日统计；网页控制台使用会话接口，不消耗此额度。">
                <NumberField
                  label="每个令牌"
                  suffix="次 / 天"
                  value={form.apiDailyLimit}
                  min={1}
                  max={1_000_000}
                  disabled={user?.role !== "super_admin"}
                  onChange={(apiDailyLimit) => setForm({ ...form, apiDailyLimit })}
                />
              </SettingRow>
              {user?.role !== "super_admin" ? <p className="settings-permission-note">仅超级管理员可以修改该项。</p> : null}
            </SettingsSection>
          </div>
          <aside className="audit-panel">
            <header><span className="eyebrow">AUDIT · PAGED</span><h2>操作记录</h2></header>
            {auditQuery.isLoading ? <Loading /> : auditQuery.error ? <ErrorState error={auditQuery.error} /> : auditQuery.data?.items.length ? (
              <><div className="audit-list">{auditQuery.data.items.map((item) => <div className="audit-item" key={item.id}><span>{date(item.createdAt, true)}</span><strong>{item.action}</strong><p>{item.actorEmail ?? "system"} · {item.entityType}{item.entityId ? ` / ${item.entityId.slice(0, 8)}` : ""}</p></div>)}</div><footer className="pagination"><span>{auditQuery.data.pagination.page} / {auditQuery.data.pagination.totalPages} · {auditQuery.data.pagination.total}</span><div><Button disabled={auditPage <= 1} onClick={() => setAuditPage((value) => value - 1)}>←</Button><Button disabled={auditPage >= auditQuery.data.pagination.totalPages} onClick={() => setAuditPage((value) => value + 1)}>→</Button></div></footer></>
            ) : <Empty title="没有审计记录" body="关键变更会写入这里。" />}
          </aside>
        </div>
      ) : null}
      <footer className="policy-note"><Settings2 size={17} /><p><strong>配置即策略。</strong> 设置保存后立即应用于 Worker 与独立服务器，不需要重启；部署级机密仍只通过环境变量或 Wrangler Secret 管理。</p></footer>
    </div>
  );
}

function SettingsSection({ index, eyebrow, title, children }: React.PropsWithChildren<{ index: string; eyebrow: string; title: string }>) {
  return <section className="settings-section"><header><span className="settings-section__index">{index}</span><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div></header><div className="settings-section__body">{children}</div></section>;
}

function SettingRow({ title, body, children }: React.PropsWithChildren<{ title: string; body: string }>) {
  return <div className="setting-row"><div><strong>{title}</strong><p>{body}</p></div>{children}</div>;
}

function NumberField({ label, suffix, value, min, max, disabled = false, onChange }: { label: string; suffix: string; value: number; min: number; max: number; disabled?: boolean; onChange: (value: number) => void }) {
  return <Field label={label}><div className="number-field"><input className="input" type="number" value={value} min={min} max={max} disabled={disabled} onChange={(event) => onChange(Number.parseInt(event.target.value || String(min), 10))} /><span>{suffix}</span></div></Field>;
}
